import Modal from '@/components/common/modal';
import { useTranslations } from '@/i18n/use-translations';
import { Typography, Stack, Button, Box, Grid, Card, CardContent, Divider, CircularProgress } from '@mui/material';
import ShoppingCartOutlined from '@mui/icons-material/ShoppingCartOutlined';
import { alpha } from '@mui/material/styles';
import { Fragment, lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { useBreakpointUp } from '@/hooks/use-breakpoint';
import { useSubscriptionPlansQuery } from '@/state/queries/transactions/use-subscription-plans';
import { usePatchEventSubscriptionCheckoutCart } from '@/state/mutations/transactions/use-patch-event-subscription-checkout-cart';
import { useEventSubscriptionCheckoutCartQuery } from '@/state/queries/transactions/use-event-subscription-checkout-cart';
import { PlanCardQuotaSteppers } from '@/components/organizations/plan/plan-card-quota-steppers';
import ManagePlanIncludesList from '@/components/organizations/plan/manage-plan-includes-list';
import type { SubscriptionPlan, SubscriptionTier } from '@/types/dtos/transactions/subscription-plan.dto';
import {
  getManagePlanFeatureListBullets,
  resolveIncludedFeatureKeyDisplayRows,
  resolveIncludedQuotaDisplayRows,
} from '@/helpers/manage-plan-feature-display';
import {
  EVENT_BASE_PLAN_TIERS,
  EVENT_CAPACITY_EVENT_STEPPABLE_QUOTA_KEYS,
  EVENT_CAPACITY_FLOW_STEPPABLE_QUOTA_KEYS,
  EVENT_CAPACITY_STEPPABLE_QUOTA_KEYS,
  EVENT_PAID_TIERS,
  EVENT_SUBSCRIPTION_PRODUCT_KEYS,
} from '@/constants/event-subscription-product-keys';
import { useNotifications } from '@/hooks/use-notifications';
import { useEventStore } from '@/state/stores/use-event-store';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { findBookedEventSubscription, tierFromEventSubscription } from '@/helpers/event-subscription-status';
import { flowMatchesScopeId, minPlayoutHoursForFlow } from '@/helpers/flow-playout-hours.helper';
import {
  computePreviewSubscriptionPrice,
  formatQuotaStepperCountLine,
  getOveragePricePerUnit,
  getPurchasedQuotaTotal,
  includedQuantityForKey,
  maxQuantityForKey,
  normalizeSubscriptionPlanOverage,
} from '@/helpers/organization-subscription-pricing';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import { usePermissionsQuery } from '@/state/queries/permissions/use-permissions';
import { EVENT_PERMISSIONS } from '@/enums/permissions.enum';

/** Lazy to avoid a circular import with `event-subscription-checkout.modal` (which embeds this dialog). */
const EventSubscriptionCheckoutModal = lazy(() => import('@/components/events/plan/event-subscription-checkout.modal'));

const PLAYOUT_HOURS_KEY = 'playout_hours';

const BASE_TIER_ORDER = [...EVENT_BASE_PLAN_TIERS] as SubscriptionTier[];
const PAID_TIER_ORDER = [...EVENT_PAID_TIERS] as SubscriptionTier[];

/** Side-by-side from `md` — manage modal is wide enough for 3 tier cards without waiting for `lg`. */
function tierCardGridSize(planCount: number): { xs: number; sm?: number; md?: number } {
  if (planCount <= 1) return { xs: 12 };
  if (planCount === 2) return { xs: 12, md: 6 };
  if (planCount === 3) return { xs: 12, md: 4 };
  return { xs: 12, sm: 6, md: 3 };
}

function normalizedPositiveQuotaAddonList(
  units: { quotaKey: string; quantity: number }[] | undefined,
): { quotaKey: string; quantity: number }[] {
  const m = new Map<string, number>();
  for (const u of units ?? []) {
    const k = String(u.quotaKey ?? '').trim();
    if (!k || u.quantity <= 0) continue;
    m.set(k, u.quantity);
  }
  return [...m.entries()].map(([quotaKey, quantity]) => ({ quotaKey, quantity }));
}

function quotaAddonListsEqual(
  a: { quotaKey: string; quantity: number }[] | undefined,
  b: { quotaKey: string; quantity: number }[] | undefined,
): boolean {
  const sa = normalizedPositiveQuotaAddonList(a).sort((x, y) => x.quotaKey.localeCompare(y.quotaKey));
  const sb = normalizedPositiveQuotaAddonList(b).sort((x, y) => x.quotaKey.localeCompare(y.quotaKey));
  if (sa.length !== sb.length) return false;
  return sa.every((row, i) => row.quotaKey === sb[i]!.quotaKey && row.quantity === sb[i]!.quantity);
}

function translateTierLabel(
  t: (key: string, params?: Record<string, string | number>) => string,
  tier: SubscriptionTier,
): string {
  const k = `subscription.organization.tiers.${tier}` as 'subscription.organization.tiers.BASIC';
  const r = t(k);
  return r === k ? tier : r;
}

type Props = {
  open: boolean;
  onClose: () => void;
  eventId: string;
  subscriptionProductKey: string;
  scopeType: string;
  scopeId: string;
  onCartSaved?: () => void;
  /**
   * Hide the header “Go to cart” CTA (e.g. when this dialog is opened from within checkout).
   * When omitted / false, the button is shown for users with monetize + update_subscription_plan.
   */
  hideGoToCart?: boolean;
  /** Optional override; defaults to opening the event subscription checkout modal. */
  onGoToCart?: () => void;
};

export default function EventManagePlanDialog({
  open,
  onClose,
  eventId,
  subscriptionProductKey,
  scopeType,
  scopeId,
  onCartSaved,
  hideGoToCart = false,
  onGoToCart,
}: Props) {
  const t = useTranslations();
  const notification = useNotifications();
  const isWideScreen = useBreakpointUp('sm');
  const {
    data: plansData,
    isSuccess,
    isPending,
  } = useSubscriptionPlansQuery({
    entityType: 'Event',
    enabled: open,
  });
  const { data: cartData } = useEventSubscriptionCheckoutCartQuery(eventId, open);
  const patchCart = usePatchEventSubscriptionCheckoutCart(eventId);
  const event = useEventStore((s) => s.event);
  const flowPlan = useFlowStore((s) => s.flowPlan);
  const storeFlow = useFlowStore((s) => s.flow);
  const { hasAccess: hasMonetizeAccess, isLoading: monetizeAccessLoading } = useFeatureAccess('monetize');
  const monetizeEnabled = !monetizeAccessLoading && hasMonetizeAccess;
  const { data: permissions } = usePermissionsQuery();
  const canUpdateSubscriptionPlan = Boolean(
    eventId && permissions?.events?.[eventId]?.includes(EVENT_PERMISSIONS.UPDATE_SUBSCRIPTION_PLAN),
  );
  const canGoToCart = monetizeEnabled && canUpdateSubscriptionPlan && !hideGoToCart;
  const hasCartLines = Boolean(cartData?.cart?.lines?.length);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const isBasePlanProduct =
    subscriptionProductKey === EVENT_SUBSCRIPTION_PRODUCT_KEYS.BASE_CAPACITY ||
    subscriptionProductKey === EVENT_SUBSCRIPTION_PRODUCT_KEYS.BASE_CAPACITY_EVENT ||
    subscriptionProductKey === EVENT_SUBSCRIPTION_PRODUCT_KEYS.BASE_CAPACITY_FLOW;
  const tierOrder = isBasePlanProduct ? BASE_TIER_ORDER : PAID_TIER_ORDER;
  const selectableTierSet = isBasePlanProduct
    ? (EVENT_BASE_PLAN_TIERS as readonly string[])
    : (EVENT_PAID_TIERS as readonly string[]);

  const existingLine = cartData?.cart?.lines?.find(
    (l) => l.subscriptionProductKey === subscriptionProductKey && l.scopeType === scopeType && l.scopeId === scopeId,
  );

  const bookedInstance = useMemo(
    () => findBookedEventSubscription(event?.subscriptions, subscriptionProductKey, scopeType, scopeId),
    [event?.subscriptions, subscriptionProductKey, scopeType, scopeId],
  );

  /** Public catalog may hide gated product lines; still show booked tier columns. */
  const catalog = useMemo(() => {
    const fromApi = (plansData?.plans ?? []).filter(
      (p) =>
        p.subscriptionProductKey === subscriptionProductKey &&
        p.billingPeriod === 'ONCE' &&
        selectableTierSet.includes(p.tier),
    );
    const byTier = new Map(fromApi.map((p) => [p.tier, p] as const));
    const bookedPlan = bookedInstance?.subscriptionPlan;
    if (
      bookedPlan?.tier &&
      selectableTierSet.includes(bookedPlan.tier) &&
      bookedPlan.subscriptionProductKey === subscriptionProductKey &&
      !byTier.has(bookedPlan.tier)
    ) {
      byTier.set(bookedPlan.tier, bookedPlan);
    }
    return [...byTier.values()];
  }, [plansData?.plans, subscriptionProductKey, selectableTierSet, bookedInstance]);

  const columnPlans = useMemo(
    () => tierOrder.map((tier) => catalog.find((p) => p.tier === tier)).filter(Boolean) as SubscriptionPlan[],
    [catalog, tierOrder],
  );

  /** Fulfilled tier on the event — only this is “Current plan”. */
  const bookedTierRaw = tierFromEventSubscription(bookedInstance);
  const bookedTier = bookedTierRaw && selectableTierSet.includes(bookedTierRaw) ? bookedTierRaw : null;
  /** Draft cart selection — shown as “In cart”, never as current. */
  const cartTier = (existingLine?.tier as SubscriptionTier | undefined) ?? null;

  /** PLAN flow for Flow-scoped capacity / playout lines — drives playout_hours floor. */
  const scopedPlanFlow = useMemo(() => {
    if (scopeType !== 'Flow' || !scopeId) return undefined;
    if (flowPlan && flowMatchesScopeId(flowPlan, scopeId)) return flowPlan;
    if (storeFlow?.mode === 'PLAN' && flowMatchesScopeId(storeFlow, scopeId)) return storeFlow;
    return event?.flows?.find((f) => flowMatchesScopeId(f, scopeId));
  }, [scopeType, scopeId, flowPlan, storeFlow, event?.flows]);

  /** Human-readable scope segment for the modal subheader (never raw Mongo ids when a title exists). */
  const scopeDisplayName = useMemo(() => {
    if (scopeType === 'Flow') {
      if (!scopedPlanFlow) return scopeId;
      return scopedPlanFlow.title?.trim() || t('myEventsList.unTitled');
    }
    if (scopeType === 'Event') {
      if (!event) return scopeId;
      return event.title?.trim() || t('myEventsList.unTitled');
    }
    return scopeId;
  }, [scopeType, scopeId, scopedPlanFlow, event, t]);

  const minPlayoutHours = useMemo(() => minPlayoutHoursForFlow(scopedPlanFlow), [scopedPlanFlow]);

  /** Purchased baseline for stepper diffs (Free included). Never entitlement balances. */
  const purchasedBookedPlan = useMemo(() => {
    const populated = bookedInstance?.subscriptionPlan;
    // Prefer a plan that still carries includes (thin populate / Free often needs catalog fallback).
    if (populated?.includedQuotas?.length) {
      return normalizeSubscriptionPlanOverage(populated);
    }
    if (bookedTier) {
      const fromCatalog = catalog.find((p) => p.tier === bookedTier);
      if (fromCatalog) return normalizeSubscriptionPlanOverage(fromCatalog);
    }
    return populated ? normalizeSubscriptionPlanOverage(populated) : undefined;
  }, [bookedInstance?.subscriptionPlan, bookedTier, catalog]);

  const getPurchasedBookedTotalForDelta = useMemo(() => {
    if (!bookedInstance && !purchasedBookedPlan) return undefined;
    return (quotaKey: string) =>
      getPurchasedQuotaTotal({
        quotaKey,
        catalogSnapshotIncluded: bookedInstance?.catalogSnapshot?.includedQuotas,
        bookedPlan: purchasedBookedPlan,
        additionalQuotaUnits: bookedInstance?.additionalQuotaUnits,
      });
  }, [bookedInstance, purchasedBookedPlan]);

  const [quotaExtrasByCard, setQuotaExtrasByCard] = useState<Record<string, Record<string, number>>>({});
  const [savingTier, setSavingTier] = useState<SubscriptionTier | null>(null);

  const resolvePlanForUiByTier = (tier: SubscriptionTier) => {
    const raw = catalog.find((p) => p.tier === tier);
    return raw ? normalizeSubscriptionPlanOverage(raw) : undefined;
  };

  /** Cart/order line wins over booked when present — manage modal must mirror the draft order. */
  const seedFromCart = existingLine != null;
  const seedTier = (seedFromCart ? cartTier : bookedTier) ?? cartTier ?? bookedTier;
  const seedUnits = seedFromCart
    ? (existingLine?.additionalQuotaUnits ?? [])
    : (bookedInstance?.additionalQuotaUnits ?? []);

  useEffect(() => {
    if (!open) {
      setQuotaExtrasByCard({});
      return;
    }
    if (!catalog.length || !seedTier) return;
    const activePlan = resolvePlanForUiByTier(seedTier);
    if (!activePlan) return;
    // Nothing to mirror from a booked plan with no add-ons; cart with empty extras still clears state.
    if (!seedFromCart && seedUnits.length === 0) return;

    setQuotaExtrasByCard(() => {
      const next: Record<string, Record<string, number>> = {};
      for (const u of seedUnits) {
        const k = String(u.quotaKey ?? '').trim();
        if (!k || u.quantity <= 0) continue;
        const includedActive = includedQuantityForKey(activePlan, k);
        const desiredTotal = includedActive + Math.max(0, u.quantity);
        for (const tier of tierOrder) {
          const planForTier = resolvePlanForUiByTier(tier);
          if (!planForTier) continue;
          const includedT = includedQuantityForKey(planForTier, k);
          const capT = maxQuantityForKey(planForTier, k);
          const maxExtraT = capT !== undefined ? Math.max(0, capT - includedT) : Number.POSITIVE_INFINITY;
          const extraT = Math.min(Math.max(0, desiredTotal - includedT), maxExtraT);
          next[tier] = { ...(next[tier] ?? {}), [k]: extraT };
        }
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, seedTier, seedFromCart, bookedInstance, existingLine, catalog.length]);

  /**
   * Seed playout extras so each paid card starts at/above the PLAN flow length (ceil hours).
   * Skipped when a cart/order line already exists — those extras are the source of truth.
   * Users may still reduce toward included/allocated afterward for price transparency; coverage
   * is warned in the UI rather than hard-blocked.
   */
  useEffect(() => {
    if (!open || !catalog.length || minPlayoutHours <= 0 || seedFromCart) return;
    setQuotaExtrasByCard((prev) => {
      let changed = false;
      const next: Record<string, Record<string, number>> = { ...prev };
      for (const tier of tierOrder) {
        const planForTier = resolvePlanForUiByTier(tier);
        if (!planForTier || planForTier.tier === 'FREE') continue;
        if (getOveragePricePerUnit(planForTier, PLAYOUT_HOURS_KEY) === undefined) continue;
        // Only seed when this card has no playout extra yet (don't fight user reductions).
        if (next[tier]?.[PLAYOUT_HOURS_KEY] !== undefined) continue;
        const includedT = includedQuantityForKey(planForTier, PLAYOUT_HOURS_KEY);
        const capT = maxQuantityForKey(planForTier, PLAYOUT_HOURS_KEY);
        const maxExtraT = capT !== undefined ? Math.max(0, capT - includedT) : Number.POSITIVE_INFINITY;
        const minExtraT = Math.min(Math.max(0, minPlayoutHours - includedT), maxExtraT);
        if (minExtraT > 0) {
          next[tier] = { ...(next[tier] ?? {}), [PLAYOUT_HOURS_KEY]: minExtraT };
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, minPlayoutHours, catalog.length, seedFromCart]);

  const setLocalExtraForCard = (sourceTier: SubscriptionTier) => (quotaKey: string, nextExtra: number) => {
    setQuotaExtrasByCard((prev) => {
      const sourcePlan = resolvePlanForUiByTier(sourceTier);
      if (!sourcePlan) {
        return { ...prev, [sourceTier]: { ...(prev[sourceTier] ?? {}), [quotaKey]: Math.max(0, nextExtra) } };
      }
      const includedSource = includedQuantityForKey(sourcePlan, quotaKey);
      const capSource = maxQuantityForKey(sourcePlan, quotaKey);
      const maxExtraSource =
        capSource !== undefined ? Math.max(0, capSource - includedSource) : Number.POSITIVE_INFINITY;
      const clampedExtra = Math.min(Math.max(0, nextExtra), maxExtraSource);
      const desiredTotal = includedSource + clampedExtra;
      const next: Record<string, Record<string, number>> = { ...prev };
      for (const tier of tierOrder) {
        const planForTier = resolvePlanForUiByTier(tier);
        if (!planForTier) continue;
        const includedT = includedQuantityForKey(planForTier, quotaKey);
        const capT = maxQuantityForKey(planForTier, quotaKey);
        const maxExtraT = capT !== undefined ? Math.max(0, capT - includedT) : Number.POSITIVE_INFINITY;
        const extraT = Math.min(Math.max(0, desiredTotal - includedT), maxExtraT);
        next[tier] = { ...(next[tier] ?? {}), [quotaKey]: extraT };
      }
      return next;
    });
  };

  const handlePlanCheckout = async (plan: SubscriptionPlan) => {
    const cardExtras = plan.tier === 'FREE' ? {} : { ...(quotaExtrasByCard[plan.tier] ?? {}) };
    const additionalQuotaUnits = Object.entries(cardExtras)
      .filter(([, qty]) => qty > 0)
      .map(([quotaKey, quantity]) => ({ quotaKey, quantity }));

    const otherLines =
      cartData?.cart?.lines?.filter(
        (l) =>
          !(l.subscriptionProductKey === subscriptionProductKey && l.scopeType === scopeType && l.scopeId === scopeId),
      ) ?? [];

    setSavingTier(plan.tier);
    try {
      await patchCart.mutateAsync({
        lines: [
          ...otherLines.map((l) => ({
            subscriptionProductKey: l.subscriptionProductKey,
            tier: l.tier,
            billingPeriod: 'ONCE' as const,
            additionalQuotaUnits: l.additionalQuotaUnits,
            scopeType: l.scopeType,
            scopeId: l.scopeId,
            parentSubscriptionInstanceId: l.parentSubscriptionInstanceId,
          })),
          {
            subscriptionProductKey,
            tier: plan.tier,
            billingPeriod: 'ONCE',
            additionalQuotaUnits,
            scopeType,
            scopeId,
          },
        ],
      });
      notification.notifySuccess(t('event.plan.cartSaved'));
      onCartSaved?.();
      onClose();
    } catch {
      notification.notifyError(t('event.plan.cartSaveError'));
    } finally {
      setSavingTier(null);
    }
  };

  const productDisplayName =
    subscriptionProductKey === EVENT_SUBSCRIPTION_PRODUCT_KEYS.BASE_CAPACITY ||
    subscriptionProductKey === EVENT_SUBSCRIPTION_PRODUCT_KEYS.BASE_CAPACITY_EVENT
      ? t('event.plan.capacityLine')
      : subscriptionProductKey === EVENT_SUBSCRIPTION_PRODUCT_KEYS.BASE_CAPACITY_FLOW
        ? t('event.plan.flowPlayoutLine')
        : subscriptionProductKey === EVENT_SUBSCRIPTION_PRODUCT_KEYS.EVENT_BROADCAST
          ? t('event.plan.broadcastLine')
          : t('event.plan.stageReadyLine');
  const dialogTitle = t('subscription.organization.planPage.managePlanDialogTitle', {
    productName: productDisplayName,
  });

  const handleGoToCart = () => {
    if (onGoToCart) {
      onGoToCart();
    } else {
      setCheckoutOpen(true);
    }
    // Close manage once cart is opening (checkout stays via parent `onGoToCart` or local state below).
    onClose();
  };

  const goToCartHeaderActions = canGoToCart ? (
    <Button
      size="small"
      variant="outlined"
      color="primary"
      startIcon={<ShoppingCartOutlined />}
      disabled={!hasCartLines}
      onClick={handleGoToCart}
      data-testid="event-manage-plan-go-to-cart"
      sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
    >
      {t('event.plan.goToCart')}
    </Button>
  ) : undefined;

  const planModalShellProps = {
    size: (isWideScreen ? 'lg' : 'sm') as 'lg' | 'sm',
    width: isWideScreen ? 1000 : undefined,
    minHeight: isWideScreen ? 360 : undefined,
    maxHeight: isWideScreen ? ('min(92vh, calc(100vh - 48px))' as const) : undefined,
    sx: isWideScreen ? ({ overflow: 'hidden' } as const) : undefined,
    fullScreen: !isWideScreen,
    contentSx: isWideScreen
      ? ({ minHeight: 0, overflowY: 'auto' as const, flex: 1 } as const)
      : ({ overflow: 'auto' as const, minHeight: 0 } as const),
    headerActions: goToCartHeaderActions,
  };

  const checkoutModal =
    canGoToCart && checkoutOpen ? (
      <Suspense fallback={null}>
        <EventSubscriptionCheckoutModal open onClose={() => setCheckoutOpen(false)} eventId={eventId} />
      </Suspense>
    ) : null;

  // Keep mounted while local checkout is open after manage closed (no parent `onGoToCart`).
  if (!open && !checkoutOpen) return null;

  if (!open) {
    return <Fragment>{checkoutModal}</Fragment>;
  }

  if (isPending || !plansData || !isSuccess) {
    return (
      <Fragment>
        <Modal
          open
          handleClose={onClose}
          title={dialogTitle}
          description={t('event.plan.onceBillingHint')}
          {...planModalShellProps}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        </Modal>
        {checkoutModal}
      </Fragment>
    );
  }

  if (!columnPlans.length) {
    return (
      <Fragment>
        <Modal
          open
          handleClose={onClose}
          title={dialogTitle}
          description={t('event.plan.onceBillingHint')}
          {...planModalShellProps}
        >
          <Typography color="text.secondary">{t('subscription.organization.planPage.noPlansInCatalog')}</Typography>
        </Modal>
        {checkoutModal}
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Modal
        open={open}
        handleClose={onClose}
        title={dialogTitle}
        description={`${t('event.plan.scopeLabel', { scopeType, scopeName: scopeDisplayName })} · ${t('event.plan.onceBillingHint')}`}
        {...planModalShellProps}
      >
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {columnPlans.map((plan, planIndex) => {
            const planForUi = normalizeSubscriptionPlanOverage(plan);
            const previousPlan = planIndex > 0 ? columnPlans[planIndex - 1] : undefined;
            const previousPlanForUi = previousPlan ? normalizeSubscriptionPlanOverage(previousPlan) : undefined;
            const cardKey = plan.tier;
            const isBooked = bookedTier != null && plan.tier === bookedTier;
            const isInCart = cartTier != null && plan.tier === cartTier && !isBooked;
            // One accent border: cart selection when present, otherwise the booked current plan.
            const highlighted = cartTier != null ? plan.tier === cartTier : isBooked;
            const baselineUnits = isBooked
              ? bookedInstance?.additionalQuotaUnits
              : isInCart
                ? existingLine?.additionalQuotaUnits
                : undefined;
            const cardExtras = quotaExtrasByCard[cardKey] ?? {};
            const cardExtrasAsAddonList = Object.entries(cardExtras)
              .filter(([, q]) => q > 0)
              .map(([quotaKey, quantity]) => ({ quotaKey, quantity }));
            const localExtrasDifferFromBaseline =
              Object.keys(cardExtras).length > 0 && !quotaAddonListsEqual(cardExtrasAsAddonList, baselineUnits);
            const activeCardHasPendingEdits = (isBooked || isInCart) && localExtrasDifferFromBaseline;
            const displayPrice = computePreviewSubscriptionPrice(planForUi, cardExtras);
            const tierLabel = translateTierLabel(t, plan.tier);
            const prevTier = previousPlan?.tier ?? plan.tier;
            const isFreeTier = plan.tier === 'FREE';
            const steppableQuotaKeys =
              subscriptionProductKey === EVENT_SUBSCRIPTION_PRODUCT_KEYS.BASE_CAPACITY
                ? EVENT_CAPACITY_STEPPABLE_QUOTA_KEYS
                : subscriptionProductKey === EVENT_SUBSCRIPTION_PRODUCT_KEYS.BASE_CAPACITY_EVENT
                  ? EVENT_CAPACITY_EVENT_STEPPABLE_QUOTA_KEYS
                  : subscriptionProductKey === EVENT_SUBSCRIPTION_PRODUCT_KEYS.BASE_CAPACITY_FLOW
                    ? EVENT_CAPACITY_FLOW_STEPPABLE_QUOTA_KEYS
                    : undefined;
            const freeQuotaRows = planForUi.includedQuotas ?? [];
            const marketingBullets = getManagePlanFeatureListBullets(t, subscriptionProductKey, plan.tier);
            const includedQuotaRows = resolveIncludedQuotaDisplayRows(
              t,
              planForUi.includedQuotas,
              previousPlanForUi?.includedQuotas,
            );
            const includedFeatureRows = resolveIncludedFeatureKeyDisplayRows(
              t,
              plan.featureKeys,
              previousPlan?.featureKeys,
            );
            const showIncludesBody =
              marketingBullets.length > 0 || includedQuotaRows.length > 0 || includedFeatureRows.length > 0;
            const includedPlayout = includedQuantityForKey(planForUi, PLAYOUT_HOURS_KEY);
            const planMaxPlayout = maxQuantityForKey(planForUi, PLAYOUT_HOURS_KEY);
            const canCoverPlayout =
              minPlayoutHours <= 0 ||
              (planMaxPlayout !== undefined
                ? planMaxPlayout >= minPlayoutHours
                : isFreeTier
                  ? includedPlayout >= minPlayoutHours
                  : getOveragePricePerUnit(planForUi, PLAYOUT_HOURS_KEY) !== undefined);

            let ctaLabel: string;
            if (isBooked) {
              ctaLabel = activeCardHasPendingEdits
                ? t('subscription.organization.planPage.updatePlanQuotas')
                : t('subscription.organization.planPage.currentPlan');
            } else if (isInCart) {
              ctaLabel = activeCardHasPendingEdits
                ? t('subscription.organization.planPage.updatePlanQuotas')
                : t('event.plan.inCart');
            } else if (bookedTier != null && bookedTier !== 'FREE') {
              ctaLabel = t('subscription.organization.planPage.upgradeOrSwitch');
            } else {
              ctaLabel = t('event.plan.choose');
            }

            return (
              <Grid size={tierCardGridSize(columnPlans.length)} key={`${plan.tier}-ONCE`}>
                <Card
                  variant="outlined"
                  sx={(theme) => ({
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderStyle: 'solid',
                    borderWidth: highlighted ? 2 : 1,
                    borderColor: highlighted ? alpha(theme.palette.primary.main, 0.38) : theme.palette.divider,
                  })}
                >
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                      <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ minWidth: 0 }}>
                        {tierLabel}
                      </Typography>
                      {/* ONCE plans: no MONTH/YEAR toggle (same as org when only one cadence exists). */}
                    </Stack>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 0.25 }}>
                      <Typography variant="h5" component="span" fontWeight={600}>
                        {displayPrice <= 0 ? t('event.plan.freePrice') : `€${displayPrice.toFixed(2)}`}
                      </Typography>
                      {displayPrice > 0 ? (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                          {`, ${t('event.checkoutModal.excludingVat')}`}
                        </Typography>
                      ) : null}
                    </Box>
                    {isFreeTier ? (
                      <Stack spacing={0.5} sx={{ maxWidth: 300 }}>
                        {freeQuotaRows.map((q) => (
                          <Typography key={q.quotaKey} variant="caption" color="text.secondary">
                            {formatQuotaStepperCountLine(t, q.quotaKey, q.quantity)}
                          </Typography>
                        ))}
                        <Typography variant="caption" color="text.secondary" sx={{ pt: 0.5 }}>
                          {t('event.plan.freeNoOverageHint')}
                        </Typography>
                        {minPlayoutHours > 0 && includedPlayout < minPlayoutHours ? (
                          <Typography variant="caption" color="warning.main" sx={{ pt: 0.5 }}>
                            {t('event.plan.playoutBelowFlowLength', { hours: minPlayoutHours })}
                          </Typography>
                        ) : null}
                      </Stack>
                    ) : (
                      <Stack spacing={0.75}>
                        <PlanCardQuotaSteppers
                          compact
                          plan={planForUi}
                          subscription={undefined}
                          balances={undefined}
                          balancesLoading={false}
                          isActivePlan={isBooked || isInCart}
                          localQuotaExtra={cardExtras}
                          onSetLocalExtra={setLocalExtraForCard(cardKey)}
                          showDeltaVsBooked={getPurchasedBookedTotalForDelta != null}
                          quotaKeys={steppableQuotaKeys}
                          tooltipContext="event"
                          getBookedTotalForDelta={getPurchasedBookedTotalForDelta}
                        />
                        {minPlayoutHours > 0 &&
                        includedPlayout + (cardExtras[PLAYOUT_HOURS_KEY] ?? 0) < minPlayoutHours ? (
                          <Typography variant="caption" color="warning.main">
                            {t('event.plan.playoutBelowFlowLength', { hours: minPlayoutHours })}
                          </Typography>
                        ) : null}
                      </Stack>
                    )}
                    <Divider />
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {planIndex === 0
                        ? t('subscription.organization.planPage.includesLabel')
                        : t('subscription.organization.planPage.everythingInPrevious', {
                            previous: translateTierLabel(t, prevTier),
                          })}
                    </Typography>
                    {showIncludesBody ? (
                      <ManagePlanIncludesList
                        productKey={subscriptionProductKey}
                        tier={plan.tier}
                        includedQuotas={planForUi.includedQuotas}
                        featureKeys={plan.featureKeys}
                        previousIncludedQuotas={previousPlanForUi?.includedQuotas}
                        previousFeatureKeys={previousPlan?.featureKeys}
                      />
                    ) : (
                      <Box sx={{ flex: 1 }} />
                    )}
                    <Button
                      type="button"
                      fullWidth
                      variant={highlighted ? 'outlined' : 'contained'}
                      size="medium"
                      disabled={
                        !canCoverPlayout ||
                        ((isBooked || isInCart) && !activeCardHasPendingEdits) ||
                        patchCart.isPending ||
                        (savingTier != null && savingTier !== plan.tier)
                      }
                      loading={savingTier === plan.tier}
                      onClick={() => void handlePlanCheckout(plan)}
                    >
                      {ctaLabel}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Modal>
      {checkoutModal}
    </Fragment>
  );
}
